/**
 * EmailAddress - RFC 2822 compliant email address formatter and validator
 * 
 * This class provides RFC 2822 compliant email address handling, including:
 * - Parsing and formatting email addresses according to RFC 2822 specification
 * - Validation of email addresses (addr-spec format)
 * - Proper handling of display names (phrase) with special character escaping
 * - Format: [display-name] <addr-spec> or just addr-spec
 * 
 * RFC 2822 Compliance:
 * - Supports name-addr format: [display-name] angle-addr
 * - Validates addr-spec according to RFC 2822: local-part "@" domain
 * - Properly escapes display names containing special characters
 * - Handles quoted strings and dot-atom formats
 * 
 * Supports two constructors:
 * 1. new EmailAddress(name, email) - Creates from name and email
 * 2. new EmailAddress(formattedEmail) - Parses from formatted string like "Name <email@domain.com>"
 * 
 * Static Methods:
 * - EmailAddress.isValidEmail(email) - Validates an email address string according to RFC 2822
 * 
 * Instance Properties:
 * - name: The display name (phrase in RFC 2822)
 * - email: The email address (addr-spec in RFC 2822)
 * - formattedEmail: RFC 2822 compliant formatted string
 * 
 * Instance Methods:
 * - isValid() - Returns true if the email address is valid according to RFC 2822
 */

class EmailAddress {
  constructor(nameOrFormatted, email) {
    // Initialize private properties
    this._name = ""
    this._email = ""
    this._formattedEmail = ""
    
    if (email === undefined) {
      // Single argument constructor - parse formatted string
      this._parseFormattedString(nameOrFormatted)
    } else {
      // Two argument constructor - name and email
      this._name = nameOrFormatted || ""
      this._email = email || ""
      this._validateAndFormat()
    }
  }

  /**
   * Parse a formatted email string according to RFC 2822
   * Format: [display-name] <addr-spec> or just addr-spec
   */
  _parseFormattedString(formatted) {
    if (!formatted || typeof formatted !== "string") {
      this._name = ""
      this._email = ""
      this._formattedEmail = ""
      return
    }

    const trimmed = formatted.trim()
    
    // Check if it's in the format "Name <email@domain.com>"
    // Use a more precise regex to avoid matching nested angle brackets
    const angleBracketMatch = trimmed.match(/^(.+?)\s*<([^<>]+)>$/)
    
    if (angleBracketMatch) {
      // Format: "Display Name <email@domain.com>"
      const potentialName = angleBracketMatch[1].trim()
      const potentialEmail = angleBracketMatch[2].trim()
      
      // Verify that the part in angle brackets is actually an email address
      if (this._isValidEmail(potentialEmail) || potentialEmail.includes("@")) {
        this._name = potentialName
        this._email = potentialEmail
      } else {
        // Not a valid email format, treat as plain string
        this._name = ""
        this._email = trimmed
      }
    } else {
      // Format: just "email@domain.com" or potentially "Name email@domain.com"
      // Try to detect if there's a name before the email
      // Look for email pattern at the end
      const emailMatch = trimmed.match(/(.+?)\s+([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/)
      
      if (emailMatch) {
        // Has name before email
        this._name = emailMatch[1].trim()
        this._email = emailMatch[2].trim()
      } else {
        // Check if the whole string is just an email
        if (this._isValidEmail(trimmed) || trimmed.includes("@")) {
          this._name = ""
          this._email = trimmed
        } else {
          // Not a valid email, treat as name only
          this._name = trimmed
          this._email = ""
        }
      }
    }

    this._validateAndFormat()
  }

  /**
   * Validate email address according to RFC 2822 addr-spec
   * addr-spec = local-part "@" domain
   */
  _isValidEmail(email) {
    if (!email || typeof email !== "string") {
      return false
    }

    // Basic RFC 2822 addr-spec validation
    // local-part can be dot-atom or quoted-string
    // domain can be dot-atom or domain-literal
    // Simplified validation - more permissive than strict RFC parsing
    const emailPattern = /^[a-zA-Z0-9.!#$%&'*+\-/=?^_`{|}~]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    return emailPattern.test(email.trim())
  }

  /**
   * Escape special characters in display name for RFC 2822 compliance
   * If display name contains special characters, it should be quoted
   */
  _escapeDisplayName(name) {
    if (!name || typeof name !== "string") {
      return ""
    }

    const trimmed = name.trim()
    
    // If name contains special characters or spaces, quote it
    // Special characters that require quoting: ( ) < > [ ] : ; @ \ , . "
    const needsQuoting = /[()<>\[\]:;@\\,."]/.test(trimmed) || trimmed.includes(" ")
    
    if (needsQuoting) {
      // Escape backslashes and quotes
      const escaped = trimmed.replace(/\\/g, "\\\\").replace(/"/g, '\\"')
      return `"${escaped}"`
    }
    
    return trimmed
  }

  /**
   * Validate and format the email address according to RFC 2822
   */
  _validateAndFormat() {
    // Clean up name and email
    this._name = (this._name || "").trim()
    let email = (this._email || "").trim()
    
    // Remove any existing angle brackets from email (shouldn't be there, but handle it)
    email = email.replace(/^<(.+)>$/, "$1").trim()

    this._email = email

    // Validate email
    if (!this._isValidEmail(this._email)) {
      // If email is invalid, still format but mark as invalid
      if (this._email) {
        this._formattedEmail = this._name ? `${this._name} <${this._email}>` : this._email
      } else {
        this._formattedEmail = this._name || ""
      }
      return
    }

    // Format according to RFC 2822: name-addr = [display-name] angle-addr
    if (this._name && this._name !== this._email) {
      // Has display name - format as "Display Name <email@domain.com>"
      const escapedName = this._escapeDisplayName(this._name)
      this._formattedEmail = `${escapedName} <${this._email}>`
    } else {
      // No display name or name equals email - just use email
      this._formattedEmail = this._email
    }
  }

  /**
   * Get the display name (property accessor)
   */
  get name() {
    return this._name || ""
  }

  set name(value) {
    this._name = value || ""
  }

  /**
   * Get the email address (property accessor)
   */
  get email() {
    return this._email || ""
  }

  set email(value) {
    this._email = value || ""
  }

  /**
   * Get the RFC 2822 formatted email string (property accessor)
   */
  get formattedEmail() {
    return this._formattedEmail || ""
  }

  set formattedEmail(value) {
    this._formattedEmail = value || ""
  }

  /**
   * Get the display name (method - for backward compatibility)
   */
  getName() {
    return this.name
  }

  /**
   * Get the email address (method - for backward compatibility)
   */
  getEmail() {
    return this.email
  }

  /**
   * Get the RFC 2822 formatted email string (method - for backward compatibility)
   */
  getFormattedEmail() {
    return this.formattedEmail
  }

  /**
   * Convert to string (returns formattedEmail)
   */
  toString() {
    return this.getFormattedEmail()
  }

  /**
   * Check if email is valid
   */
  isValid() {
    return this._isValidEmail(this._email)
  }

  /**
   * Static method to validate an email address string according to RFC 2822
   * 
   * This method validates the addr-spec format according to RFC 2822:
   * - addr-spec = local-part "@" domain
   * - local-part can be dot-atom or quoted-string
   * - domain can be dot-atom or domain-literal
   * 
   * @param {string} email - The email address string to validate
   * @returns {boolean} - True if the email address is valid according to RFC 2822
   * 
   * @example
   * EmailAddress.isValidEmail("user@example.com") // returns true
   * EmailAddress.isValidEmail("invalid-email") // returns false
   */
  static isValidEmail(email) {
    if (!email || typeof email !== "string") {
      return false
    }

    // RFC 2822 addr-spec validation
    // local-part can be dot-atom or quoted-string
    // domain can be dot-atom or domain-literal
    // This pattern matches RFC 2822 compliant email addresses
    const emailPattern = /^[a-zA-Z0-9.!#$%&'*+\-/=?^_`{|}~]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    return emailPattern.test(email.trim())
  }
}

export default EmailAddress
